/**
 * Observability + cost telemetry for one streamed Meridian turn.
 *
 * SEAM: these three AI SDK callbacks are the only place the turn REPORTS on
 * itself — they never change what the model is asked to do, and the streaming
 * orchestration never reads what they emit. They move together because they
 * change for one reason: what we need to see about a turn (Slack error routing,
 * cache-hit visibility, per-step token attribution, `meridian_usage_events`
 * pricing rows). Lifting them out of the `streamConfig` object literal also
 * keeps the dynamic `usage-events` import — deliberately lazy so the
 * Supabase/uuid query graph stays out of the bedrock module's static load
 * path — next to the only code that uses it.
 *
 * Re-exported from `./bedrock-client` so every existing import path still works.
 */
import { logStepUsage, computeCost } from '@west-tennessee-consulting/logger';
// Type-only import (erased at compile) — the runtime `createUsageEvents` is
// loaded via dynamic import inside the finish handler so the Supabase/uuid query
// graph stays OUT of this module's static load path. Pulling it in statically
// drags `uuid` (ESM) into every jest suite that imports bedrock, which jest
// can't transform. Matches the existing lazy-import pattern in stream-executor.ts.
import type { UsageEventInsert } from '../db/queries/usage-events/index.js';
import type { StepBudget } from './step-budget.js';
import type { BedrockErrorLike } from './message-types.js';
import { logger } from '../logger.js';
import { resolveChatSurface, type ChatSurface } from '../chat-surface.js';

/** Log fields correlating every line of a turn to its chat/user. */
export type TurnLogContext = Readonly<Record<string, unknown>>;

/** Everything the finish handler needs that it used to close over. */
export interface TurnTelemetryContext {
  logCtx: TurnLogContext;
  /** Model id used for the `computeCost` pricing lookup. */
  modelId: string;
  /**
   * The backend that actually served the turn ('aws' | 'deepseek'). Every row
   * in the ledger used to say 'bedrock' — including DeepSeek turns, which never
   * touch Bedrock — so the column could not answer the one question it exists
   * for. Optional for callers that have not resolved a provider.
   */
  providerKind?: string;
  /**
   * Which CLIENT sent the turn, from the `X-WTC-Client` header. Optional and
   * fed straight to `resolveChatSurface`, which falls back to the chat id when
   * it is absent — so a caller that does not know stays on today's behaviour.
   */
  clientSurface?: ChatSurface;
  stepBudget: StepBudget;
  chatId?: string;
  userId?: string;
  /**
   * Called when the loop stopped because it RAN OUT of budget while the model
   * still wanted to act — the step cap or the wall-clock deadline fired, not a
   * natural finish. Never called on a normal completion.
   */
  onBudgetExhausted?: (info: {
    reason: 'step-cap' | 'deadline';
    stepCount: number;
    maxSteps: number;
  }) => void;
}

export interface StepLike {
  finishReason?: string;
  text?: string;
  toolCalls?: Array<{ toolName?: string }>;
  toolResults?: unknown[];
  usage?: { inputTokens?: number; outputTokens?: number; cachedInputTokens?: number };
  providerMetadata?: { anthropic?: { cacheCreationInputTokens?: number; cacheReadInputTokens?: number } };
}
export interface UsageLike {
  inputTokens?: number;
  outputTokens?: number;
  cachedInputTokens?: number;
}
export interface FinishLike {
  finishReason?: string;
  text?: string;
  steps?: StepLike[];
  usage?: UsageLike;
  providerMetadata?: { anthropic?: { cacheCreationInputTokens?: number; cacheReadInputTokens?: number } };
}

// Stream-time error reporting. The provider request is made lazily when the
// stream is CONSUMED downstream, not at the `await streamText()` call — so a
// mid-stream failure (Anthropic/Bedrock 400 rejecting the tools array, a
// retry-exhausted 429) never reaches the synchronous try/catch below. Without
// an explicit onError, the AI SDK's DEFAULT handler only `console.error`s the
// raw AI_APICallError: it never flows through @west-tennessee-consulting/logger, so the #Hub Errors
// Slack sink (which forwards logger.error/fatal entries) never fires and the
// failure is invisible outside Vercel logs. Routing it through logger.error
// here restores Slack reporting for every streaming failure. The SDK still
// surfaces the error to the client via createUIMessageStream's onError.
export function createStreamErrorHandler(logCtx: TurnLogContext): (event: { error: unknown }) => void {
  return ({ error }) => {
    const err = error as Error & BedrockErrorLike;
    const isValidation =
      err?.name === 'ValidationException' ||
      String(err?.message ?? '').includes('ValidationException');
    logger.error('meridian stream error', error instanceof Error ? error : undefined, {
      ...logCtx,
      op: 'streamBedrockResponse.onError',
      isValidationException: isValidation,
      errorName: err?.name,
      statusCode: err?.statusCode,
      requestId: err?.requestId,
      ...(error instanceof Error ? {} : { rawError: String(error) }),
    });
  };
}

// Per-step observability. streamResult.finishReason/.steps/.usage are
// PROMISES that resolve only after the stream is consumed downstream, so
// reading them in the wrapper logs unresolved promises. These callbacks
// fire with RESOLVED values at the right time — the only reliable window
// into the agentic tool loop (which tools ran each step, why it stopped).
export function createStepFinishHandler(logCtx: TurnLogContext): (step: StepLike) => void {
  return (step: StepLike) => {
    logger.info('meridian step finished', {
      ...logCtx,
      op: 'streamBedrockResponse.onStepFinish',
      finishReason: step.finishReason,
      toolCalls: (step.toolCalls ?? []).map((tc) => tc.toolName),
      toolResultCount: (step.toolResults ?? []).length,
      textLength: step.text?.length ?? 0,
    });
  };
}

/**
 * Turn-completion handler: classifies WHY the turn ended (stuck loop / step cap
 * / wall-clock deadline / normal), reports cache-hit numbers, and emits + persists
 * per-step token & cost telemetry.
 */
export function createFinishHandler(turn: TurnTelemetryContext): (event: FinishLike) => void {
  return (event: FinishLike) => {
    const steps = event.steps ?? [];
    const toolCallsPerStep = steps.map((s) =>
      (s.toolCalls ?? []).map((tc) => tc.toolName ?? 'unknown'),
    );
    const allToolCalls = toolCallsPerStep.flat();
    const finalTextLength = event.text?.length ?? 0;
    const usedToolSearch = allToolCalls.includes('tool_search');
    // Cache observability — the ONLY way to confirm prompt caching is working
    // (and thus keeping us under the ITPM limit). Cache reads are ITPM-exempt;
    // if these stay 0 across a multi-step turn, caching is NOT hitting.
    const anthropicMeta = event.providerMetadata?.anthropic ?? {};
    const cacheReadInputTokens = anthropicMeta.cacheReadInputTokens ?? event.usage?.cachedInputTokens ?? 0;
    const cacheCreationInputTokens = anthropicMeta.cacheCreationInputTokens ?? 0;
    const ctx = {
      ...turn.logCtx,
      op: 'streamBedrockResponse.onFinish',
      finishReason: event.finishReason,
      stepCount: steps.length,
      stepCountLimit: turn.stepBudget.maxSteps,
      turnDeadlineMs: turn.stepBudget.deadlineMs,
      totalToolCalls: allToolCalls.length,
      distinctTools: [...new Set(allToolCalls)],
      toolCallsPerStep,
      usedToolSearch,
      finalTextLength,
      inputTokens: event.usage?.inputTokens ?? 0,
      outputTokens: event.usage?.outputTokens ?? 0,
      cacheReadInputTokens,
      cacheCreationInputTokens,
      cacheHit: cacheReadInputTokens > 0,
    };

    // Stuck-loop signature: the model called tool(s) but the turn produced
    // NO final assistant text — i.e. it wanted to keep acting but the loop
    // ended (e.g. the deferred-tool-search history break, or the step cap).
    // This is the "won't run more than one tool after find_company" bug —
    // surface it loudly so it's not invisible again. Note: `tool-calls`
    // finishReason alone is a NORMAL HITL pause (approval tool), so we gate
    // on "no final text" to avoid false alarms on legitimate pauses.
    if (allToolCalls.length > 0 && finalTextLength === 0) {
      logger.warn('meridian turn ended after tool calls with no final response', ctx);
    } else if (steps.length >= turn.stepBudget.maxSteps) {
      logger.warn('meridian turn hit the step-count limit', ctx);
      turn.onBudgetExhausted?.({
        reason: 'step-cap',
        stepCount: steps.length,
        maxSteps: turn.stepBudget.maxSteps,
      });
    } else if (event.finishReason === 'tool-calls') {
      // Stopped between steps while the model still wanted to act, but the
      // step cap was NOT reached — on this path that means the wall-clock
      // deadline fired. Distinct from the step-cap warning because the fix is
      // different: raise MERIDIAN_TURN_DEADLINE_MS / route to a background
      // turn, not raise the step cap.
      logger.warn('meridian turn stopped early (wall-clock deadline)', ctx);
      turn.onBudgetExhausted?.({
        reason: 'deadline',
        stepCount: steps.length,
        maxSteps: turn.stepBudget.maxSteps,
      });
    } else {
      logger.info('meridian turn finished', ctx);
    }

    // Per-step token & cost telemetry — one row per model request (step),
    // tagged with the tools that step invoked so spend is attributable at
    // a tool level. Emitted as `model.step.usage` logs AND persisted to
    // meridian_usage_events. Wrapped so a telemetry failure never breaks
    // the chat turn; the DB write is fire-and-forget.
    try {
      const chatId = turn.chatId;
      const userId = turn.userId;
      const usageEvents: UsageEventInsert[] = [];

      steps.forEach((s, stepIndex) => {
        const stepTools = (s.toolCalls ?? []).map((tc) => tc.toolName ?? 'unknown');
        const stepMeta = s.providerMetadata?.anthropic ?? {};
        const stepUsage = {
          inputTokens: Number(s.usage?.inputTokens ?? 0),
          outputTokens: Number(s.usage?.outputTokens ?? 0),
          cacheCreationInputTokens: Number(stepMeta.cacheCreationInputTokens ?? 0),
          cacheReadInputTokens: Number(
            stepMeta.cacheReadInputTokens ?? s.usage?.cachedInputTokens ?? 0,
          ),
        };

        // Skip fully-empty steps — nothing to attribute or bill.
        if (stepUsage.inputTokens === 0 && stepUsage.outputTokens === 0) return;

        logStepUsage(logger, {
          modelId: turn.modelId,
          provider: turn.providerKind ?? 'bedrock',
          chatId,
          userId,
          stepIndex,
          tools: stepTools,
          finishReason: s.finishReason,
          usage: stepUsage,
        });

        // Persist only when we have a chat to attribute the row to.
        if (chatId) {
          const breakdown = computeCost(turn.modelId, stepUsage);
          usageEvents.push({
            chat_id: chatId,
            user_id: userId ?? null,
            // `surface` keeps its historical value ('meridian-chat', which
            // v_meridian_daily_cost depends on); WHICH chat client this was
            // rides `feature`, the fine-grained field. Without it a Slack turn
            // and a Hub turn are indistinguishable in the cost ledger.
            feature: resolveChatSurface(chatId, turn.clientSurface),
            provider: turn.providerKind ?? 'bedrock',
            model_id: breakdown.modelId,
            canonical_model_id: breakdown.canonicalModelId,
            step_index: stepIndex,
            tools: stepTools,
            finish_reason: s.finishReason ?? null,
            input_tokens: breakdown.inputTokens,
            output_tokens: breakdown.outputTokens,
            cache_creation_input_tokens: breakdown.cacheCreationInputTokens,
            cache_read_input_tokens: breakdown.cacheReadInputTokens,
            total_tokens: breakdown.totalTokens,
            input_cost_usd: breakdown.inputCostUsd,
            output_cost_usd: breakdown.outputCostUsd,
            cache_write_cost_usd: breakdown.cacheWriteCostUsd,
            cache_read_cost_usd: breakdown.cacheReadCostUsd,
            total_cost_usd: breakdown.totalCostUsd,
            pricing_unknown: breakdown.pricingUnknown,
          });
        }
      });

      if (usageEvents.length > 0) {
        // Fire-and-forget: telemetry must never block or fail the turn.
        // Dynamic import keeps the Supabase/uuid query graph out of this
        // module's static load path (see the import note above).
        void import('../db/queries/usage-events/index.js')
          .then((m) => m.createUsageEvents(usageEvents))
          .catch((err) => {
            logger.warn('failed to persist meridian usage events', {
              op: 'streamBedrockResponse.onFinish',
              chatId,
              error: String(err),
            });
          });
      }
    } catch (usageErr) {
      logger.warn('failed to emit per-step usage telemetry', {
        op: 'streamBedrockResponse.onFinish',
        error: String(usageErr),
      });
    }
  };
}
